import { lazy, Suspense, useRef, useState } from 'react';
import { useLicense } from '../license/context';
import { ProLock, UpgradePrompt } from '../license/UpgradePrompt';
import './TranscribeButton.css';

// The dialog (and the transcribe Web Worker + @huggingface/transformers it
// drags in via `workerRunner.ts`) is a lazy chunk (cue-forge-sdv.11.4), mounted
// only once a Pro user actually opens it — the same pattern as
// `CustomPresetEditor`/`AdvancedEditDialog`.
const TranscribeDialog = lazy(() =>
  import('./TranscribeDialog').then((m) => ({ default: m.TranscribeDialog })),
);

/**
 * Pro-gated trigger for on-device transcription (cue-forge-sdv.11.4, spec
 * §7.5): visible-but-locked for free users — a compact inline `UpgradePrompt`,
 * no dialog ever mounts. Shared by the Studio toolbar (an already-loaded
 * document) and the empty-state screen (transcription as a first-run,
 * doc-creating flow — see EMPTY-START in `TranscribeDialog`), since both need
 * the identical gate and lazy-loading behaviour.
 */
export function TranscribeButton() {
  const { isPro } = useLicense();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="cf-transcribe-trigger">
      <button
        ref={triggerRef}
        type="button"
        className="cf-btn"
        onClick={() => {
          if (isPro) setOpen(true);
        }}
      >
        Transcribe{'…'}
        {!isPro && (
          <>
            {' '}
            <ProLock />
          </>
        )}
      </button>
      {!isPro && <UpgradePrompt feature="Transcription" surface="transcribe" compact />}
      {isPro && open && (
        <Suspense fallback={null}>
          <TranscribeDialog
            onClose={() => {
              setOpen(false);
              triggerRef.current?.focus();
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
